import {
  AccountBox,
  AddCircle,
  AirlineStops,
  CancelOutlined,
  CheckBoxOutlined,
  CloseOutlined,
  Download,
  Edit,
  FileCopy,
  HourglassEmpty,
  NoMeetingRoomOutlined,
  NotInterestedOutlined,
} from '@mui/icons-material';
import { Button, CircularProgress, Grid, IconButton, Paper, Tooltip, useTheme } from '@mui/material';
import { useConfigContext, useTableContext, useTableState } from 'hooks';
import MaterialReactTable from 'material-react-table';
import { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useHistory } from 'react-router';
import { Link } from 'react-router-dom';
import { paths } from 'routes';
import { ComonEnums } from 'services/comon';
import { ExportActioner } from 'services/export/actioner';
import { MatchingActioner } from 'services/matching';
import { StudentActioner, StudentEnums } from 'services/student';
import { tokens } from 'themes';

function StudentListComponent(props) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [data, setData] = useState({ loading: false, docs: [], matchings: [] });
  const history = useHistory();
  const tableRef = useTableContext();
  const tableState = useTableState('student_list');
  const { campaign, site, config } = useConfigContext();

  const interventions = useMemo(() => ComonEnums.getOtherInterventionList(), []);
  const studentTypes = useMemo(() => StudentEnums.getTypeArray(), []);
  const studentTypeLabels = useMemo(() => studentTypes.map(t => t.label), [studentTypes]);

  useEffect(() => {
    setData(f => ({ ...f, loading: true }));
  }, [config]);

  useEffect(() => {
    if (data.loading) {
      StudentActioner.list(campaign, site).then(docs => {
        MatchingActioner.list(campaign, site).then(matchings => {
          setData({ loading: false, docs, matchings });
        });
      });
    }
    return () => {};
  }, [data.loading, campaign, site]);

  const {
    messages: {
      scenes: {
        student: { list: intl },
      },
    },
  } = useIntl();
  const columnTitles = intl.columnTitles;

  const columns = useMemo(
    () => [
      {
        header: 'Statut',
        id: 'draft',
        accessorFn: row => {
          if (row.stopped) return 'Arrêté';
          if (row.external) return 'Externe';
          if (row.draft) return 'Brouillon';
          return 'Validé';
        },
        Cell: ({ row }) => {
          const stopped = row.original.stopped;
          const external = row.original.external;
          const draft = row.original.draft;
          if (stopped) {
            return (
              <Tooltip title="Arrêté" arrow>
                <CancelOutlined sx={{ color: colors.redAccent[700] }} />
              </Tooltip>
            );
          }
          if (external) {
            return (
              <Tooltip title="Externe" arrow>
                <NoMeetingRoomOutlined sx={{ color: colors.redAccent[700] }} />
              </Tooltip>
            );
          }
          if (draft) {
            return (
              <Tooltip title="Brouillon" arrow>
                <HourglassEmpty sx={{ color: colors.redAccent[500] }} />
              </Tooltip>
            );
          }
          return (
            <Tooltip title="Validé" arrow>
              <CheckBoxOutlined sx={{ color: colors.greenAccent[400] }} />
            </Tooltip>
          );
        },
        filterVariant: 'select',
        filterSelectOptions: ['Arrêté', 'Externe', 'Brouillon', 'Validé'],
      },
      {
        header: 'Matching',
        id: 'matching',
        accessorFn: row => {
          const matching = data.matchings.find(m => m.students?.includes(row._id));
          const status = matching?.status;
          if (!status) {
            return (
              <Tooltip title="Pas de matching" arrow>
                <NotInterestedOutlined sx={{ color: colors.redAccent[700] }} />
              </Tooltip>
            );
          }
          if (status === 'CLOSED') {
            return (
              <Tooltip title="Matching fermé" arrow>
                <CloseOutlined sx={{ color: colors.redAccent[700] }} />
              </Tooltip>
            );
          }
          if (['DRAFT', 'VALIDED'].includes(status)) {
            return (
              <Tooltip title="Matching en cours de validation" arrow>
                <HourglassEmpty sx={{ color: colors.grey[500] }} />
              </Tooltip>
            );
          }
          return (
            <Tooltip title="Matching validé" arrow>
              {<CheckBoxOutlined sx={{ color: colors.greenAccent[400] }} />}
            </Tooltip>
          );
        },
        filterVariant: 'select',
        filterSelectOptions: [
          'Pas de matching',
          'Matching en cours de validation',
          'Matching validé',
          'Matching fermé',
        ],
        filterFn: 'customMatchingFilter',
      },
      {
        header: columnTitles.type,
        id: 'type',
        accessorFn: row => {
          const typeObj = studentTypes.find(t => t.value === row.type);
          return typeObj ? typeObj.label : row.type;
        },
        filterVariant: 'select',
        filterSelectOptions: studentTypeLabels,
        Cell: ({ row }) => {
          const typeObj = studentTypes.find(t => t.value === row.original.type);
          const typeLabel = typeObj ? typeObj.label : row.original.type;
          if (['AS', 'CC'].includes(row.original.type) && row.original.school?.class_room?.length > 0) {
            return `${typeLabel} - ${row.original.school.class_room?.filter(c => c !== '').join(' - ') ?? ''}`;
          }
          return typeLabel;
        },
      },
      {
        header: columnTitles.other_intervention,
        accessorKey: 'other_intervention',
        Cell: ({ cell }) => (
          <div>
            {cell.getValue() !== undefined && Array.isArray(cell.getValue())
              ? cell.getValue().join(' - ')
              : cell.getValue()}
          </div>
        ),
        filterVariant: 'select',
        filterSelectOptions: interventions,
      },
      {
        header: columnTitles.mobile,
        accessorKey: 'general_information.mobile',
        Cell: ({ cell, row }) => {
          let mobile = cell.getValue();
          if (
            ['AS', 'CC'].includes(row.original.type) &&
            ((row.original.general_information.mum_mobile !== undefined &&
              row.original.general_information.mum_mobile !== '') ||
              (row.original.general_information.dad_mobile !== undefined &&
                row.original.general_information.dad_mobile !== ''))
          ) {
            mobile =
              row.original.general_information.mum_mobile !== undefined &&
              row.original.general_information.mum_mobile !== ''
                ? row.original.general_information.mum_mobile
                : row.original.general_information.dad_mobile !== undefined &&
                  row.original.general_information.dad_mobile !== ''
                ? row.original.general_information.dad_mobile
                : cell.getValue();
          }
          return mobile ?? '';
        },
        enableColumnFilter: false,
      },
      {
        header: columnTitles.last_name,
        accessorKey: 'general_information.last_name',
        enableColumnFilter: false,
      },
      {
        header: columnTitles.first_name,
        accessorKey: 'general_information.first_name',
        enableColumnFilter: false,
      },
      {
        header: columnTitles.email,
        accessorKey: 'general_information',
        Cell: ({ cell }) => cell.getValue()?.email?.email ?? '',
        enableColumnFilter: false,
      },
      {
        header: columnTitles.nbInterviews,
        accessorKey: 'nb_interviews',
        enableColumnFilter: false,
      },
    ],
    [
      columnTitles.type,
      columnTitles.other_intervention,
      columnTitles.mobile,
      columnTitles.last_name,
      columnTitles.first_name,
      columnTitles.email,
      columnTitles.nbInterviews,
      studentTypes,
      studentTypeLabels,
      interventions,
      colors.greenAccent,
      colors.redAccent,
      colors.grey,
      data.matchings,
    ],
  );

  if (data.loading) {
    return (
      <Paper className="padding-small">
        <Grid
          container
          spacing={0}
          direction="column"
          alignItems="center"
          justifyContent="center"
          className="height-circular"
        >
          <CircularProgress color="primary" />
        </Grid>
      </Paper>
    );
  }

  return (
    <MaterialReactTable
      columns={columns}
      data={data.docs}
      enableDensityToggle={false}
      muiTablePaginationProps={{
        rowsPerPageOptions: [10, 25, 50, 100, 150, { label: 'All', value: data?.docs.length ?? 0 }],
      }}
      muiTableBodyCellProps={{
        //simple styling with the `sx` prop, works just like a style prop in this example
        sx: {
          fontWeight: 'normal',
          fontSize: '110%',
        },
      }}
      positionActionsColumn="last"
      positionGlobalFilter="left" //show the global filter on the left side of the top toolbar
      initialState={{
        showGlobalFilter: true, //show the global filter by default
        density: 'compact',
        pagination: {
          pageSize: 25,
        },
      }}
      filterFns={{
        customMatchingFilter: (row, id, filterValue) => {
          const matching = data.matchings.find(m => m.students?.includes(row.original._id));
          const status = matching?.status;
          if (filterValue === 'Pas de matching') {
            return !status;
          }
          if (filterValue === 'Matching en cours de validation') {
            return ['DRAFT', 'VALIDED'].includes(status);
          }
          if (filterValue === 'Matching validé') {
            return ['POSITIONED', 'LOCK'].includes(status);
          }
          if (filterValue === 'Matching fermé') {
            return status === 'CLOSED';
          }
          return false;
        },
      }}
      enableRowActions={true}
      renderRowActions={({ row }, index) => {
        return (
          <div key={index}>
            <IconButton size="small" component={Link} to={paths.front.student.edit.replace(':id', row.original._id)}>
              <Edit color="primary" />
            </IconButton>
            <IconButton size="small" component={Link} to={paths.front.student.profil.replace(':id', row.original._id)}>
              <AccountBox color="primary" />
            </IconButton>
            <IconButton
              size="small"
              component={Link}
              to={{ pathname: paths.front.student.create, state: { duplicate: true, id: row.original._id } }}
            >
              <FileCopy color="primary" />
            </IconButton>
            <IconButton
              size="small"
              component={Link}
              to={{ pathname: paths.front.student.create, state: { register: true, id: row.original._id } }}
            >
              <AirlineStops color="primary" />
            </IconButton>
          </div>
        );
      }}
      enableToolbarInternalActions={true}
      renderTopToolbarCustomActions={() => {
        return (
          <div style={{ display: 'flex', gap: '8px' }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddCircle />}
              onClick={() => history.push(paths.front.student.create)}
            >
              {intl.button.add}
            </Button>
            <Button
              variant="contained"
              color="success"
              startIcon={<Download />}
              onClick={() => ExportActioner.download('students', { campaign, site })}
              disabled={data.loading}
            >
              {intl.button.export}
            </Button>
          </div>
        );
      }}
      tableInstanceRef={tableRef}
      autoResetPageIndex={false}
      {...tableState}
    />
  );
}

const StudentList = StudentListComponent;

export { StudentList };
